import { useCallback, useEffect, useState } from "react";
import { api, fmtMoney, parseApiError } from "@/lib/api";
import { PageHeader } from "./AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { DataStateRow } from "@/components/ui/data-state";
import { DestructiveConfirmDialog } from "@/components/ui/destructive-confirm-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Package, Truck, UserSquare2, Search } from "lucide-react";

const PAGE_SIZE = 50;
const KINDS = {
  suppliers: { label: "Nhà cung cấp (NCC)", icon: Truck, cols: ["code","name","tax_code","contact","phone","address"], labels: ["Mã","Tên NCC","MST","Liên hệ","SĐT","Địa chỉ"] },
  customers: { label: "Khách hàng (KH)", icon: UserSquare2, cols: ["code","name","type","contact","phone","address"], labels: ["Mã","Tên KH","Loại","Liên hệ","SĐT","Địa chỉ"] },
  skus:      { label: "Sản phẩm (SKU)", icon: Package, cols: ["code","name","unit","category","price","cost","stock"], labels: ["Mã SP","Tên","Đơn vị","Nhóm","Giá bán","Giá vốn","Tồn"] },
};

export default function MasterData() {
  const [tab, setTab] = useState("skus");

  return (
    <div data-testid="page-master">
      <PageHeader kicker="Tổ chức" title="Master Data" breadcrumb={["Tổ chức", "Master Data"]} />
      <div className="p-6 lg:p-8">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-stone-100 rounded-sm">
            {Object.entries(KINDS).map(([k, v]) => (
              <TabsTrigger key={k} value={k} className="rounded-sm data-[state=active]:bg-white data-[state=active]:text-emerald-900" data-testid={`tab-${k}`}>
                <v.icon className="w-4 h-4 mr-1.5" />{v.label}
              </TabsTrigger>
            ))}
          </TabsList>
          {Object.keys(KINDS).map((k) => (
            <TabsContent key={k} value={k} className="mt-4">
              <MasterTable kind={k} active={tab === k} />
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </div>
  );
}

function MasterTable({ kind, active }) {
  const conf = KINDS[kind];
  const [data, setData] = useState([]);
  const [q, setQ] = useState("");
  const [page, setPage] = useState(0);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [editId, setEditId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const params = { limit: PAGE_SIZE, offset: page * PAGE_SIZE };
      const cleanQ = q.trim();
      if (cleanQ) params.q = cleanQ;
      const r = await api.get(`/master/${kind}`, { params });
      setData(r.data);
    } catch (e) {
      setLoadError(parseApiError(e, `Không tải được ${conf.label}`).message);
    } finally {
      setLoading(false);
    }
  }, [kind, conf.label, q, page]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (active) setPage(0);
  }, [active, kind]);

  const openCreate = () => { setForm({}); setEditId(null); setOpen(true); };
  const openEdit = (row) => { setForm(row); setEditId(row.id); setOpen(true); };

  const save = async () => {
    try {
      const payload = { ...form };
      ["price","cost","stock"].forEach((k) => { if (payload[k] !== undefined) payload[k] = Number(payload[k] || 0); });
      if (editId) await api.put(`/master/${kind}/${editId}`, payload);
      else await api.post(`/master/${kind}`, payload);
      toast.success("Đã lưu"); setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Lưu thất bại"); }
  };
  const remove = async (id) => {
    await api.delete(`/master/${kind}/${id}`); toast.success("Đã xóa"); load();
  };
  const hasPreviousPage = page > 0;
  const hasNextPage = data.length === PAGE_SIZE;

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
        <div className="relative max-w-sm flex-1">
          <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-stone-400" />
          <Input
            placeholder={`Tìm ${conf.label.toLowerCase()}...`}
            value={q}
            onChange={(e) => { setQ(e.target.value); setPage(0); }}
            className="pl-8 h-9 rounded-sm"
            data-testid={`master-search-${kind}`}
          />
        </div>
        <div className="flex items-center gap-3">
          <div className="text-sm text-stone-500 font-mono">Trang {page + 1} · {data.length} bản ghi</div>
        <Button onClick={openCreate} size="sm" className="bg-emerald-800 hover:bg-emerald-900 rounded-sm" data-testid={`btn-create-${kind}`}>
          <Plus className="w-4 h-4 mr-1" /> Thêm
        </Button>
        </div>
      </div>
      <div className="bg-white border border-stone-200 rounded-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-stone-50 border-b border-stone-200">
            <tr>
              {conf.labels.map((l) => <th key={l} className="text-left px-4 py-2.5 text-[10px] uppercase tracking-wider text-stone-600 font-bold">{l}</th>)}
              <th></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-stone-100">
            {!loading && !loadError && data.map((r) => (
              <tr key={r.id} className="hover:bg-stone-50" data-testid={`row-${kind}-${r.code}`}>
                {conf.cols.map((c) => (
                  <td key={c} className="px-4 py-2">
                    {["price","cost"].includes(c) ? <span className="font-mono text-xs">{fmtMoney(r[c])}</span>
                     : c === "stock" ? <span className="font-mono text-xs">{r[c]}</span>
                     : c === "code" ? <span className="font-mono text-xs text-stone-600">{r[c]}</span>
                     : c === "name" ? <span className="font-semibold">{r[c]}</span>
                     : <span className="text-stone-600">{r[c] || "—"}</span>}
                  </td>
                ))}
                <td className="px-4 py-2 text-right whitespace-nowrap">
                  <button onClick={() => openEdit(r)} className="text-stone-600 hover:text-emerald-800 mr-2"><Pencil className="w-4 h-4" /></button>
                  <DestructiveConfirmDialog
                    title="Xóa bản ghi?"
                    description={`${conf.label} ${r.name || r.code || r.id} sẽ bị xóa.`}
                    confirmLabel="Xóa bản ghi"
                    token={String(r.code || r.name || r.id)}
                    onConfirm={() => remove(r.id)}
                    trigger={(
                      <button className="text-stone-600 hover:text-red-700"><Trash2 className="w-4 h-4" /></button>
                    )}
                  />
                </td>
              </tr>
            ))}
            {loading && <DataStateRow colSpan={conf.labels.length + 1} type="loading" message={`Đang tải ${conf.label}...`} />}
            {!loading && loadError && <DataStateRow colSpan={conf.labels.length + 1} type="error" message={loadError} onRetry={load} />}
            {!loading && !loadError && data.length === 0 && <DataStateRow colSpan={conf.labels.length + 1} type="empty" message={`Chưa có ${conf.label}`} />}
          </tbody>
        </table>
        <div className="px-4 py-3 border-t border-stone-200 flex items-center justify-end gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={!hasPreviousPage || loading}
            onClick={() => setPage((current) => Math.max(0, current - 1))}
            className="rounded-sm"
            data-testid={`master-prev-page-${kind}`}
          >
            Trước
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={!hasNextPage || loading}
            onClick={() => setPage((current) => current + 1)}
            className="rounded-sm"
            data-testid={`master-next-page-${kind}`}
          >
            Sau
          </Button>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm">
          <DialogHeader><DialogTitle className="font-serif text-2xl">{editId ? "Sửa" : "Thêm"} {conf.label}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            {conf.cols.map((c, i) => (
              <div key={c}>
                <Label className="text-xs">{conf.labels[i]}</Label>
                <Input
                  type={["price","cost","stock"].includes(c) ? "number" : "text"}
                  value={form[c] ?? ""}
                  onChange={(e) => setForm((f) => ({ ...f, [c]: e.target.value }))}
                  className="h-9 rounded-sm mt-1"
                  data-testid={`f-${kind}-${c}`}
                />
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Hủy</Button>
            <Button onClick={save} className="bg-emerald-800 hover:bg-emerald-900 rounded-sm" data-testid="btn-save-master">Lưu</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
